import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useRef } from "react";
import uniqueId from "lodash/uniqueId";
import { useStringMap } from "../utilities/string-map/useStringMap";
import { MenuListContext } from "../menu-list/MenuListContext";
import { combineRefs } from "../combine-refs";
import { useMenuListItem } from "./useMenuListItem";
import { InteractiveMenuListItem } from "./InteractiveMenuListItem";
import { MenuListItemCheckBox } from "./MenuListItemCheckBox";
import { MenuListItemRadio } from "./MenuListItemRadio";
import { MenuListItemDetails } from "./MenuListItemDetails";
import * as Styled from "./MenuListItem.styled";
import { stringMapDefault } from "./MenuListItem.stringMap";
import { useClickThrough } from "./useClickThrough";
import { useNavigationElement } from "./useNavigationElement";
// // FIXME: update to use correct type, make type reusable
// type StringMap = {
//   [Property in keyof Exclude<MenuListItemProps["stringMap"], undefined>]-?: Exclude<
//     MenuListItemProps["stringMap"],
//     undefined
//   >[Property];
// };
const scrubStringForId = (valueString) => valueString.replace(/[^a\w\-:.]+/g, "-");
const componentName = "MenuListItem";
export const MenuListItem = forwardRef((props, ref) => {
    const { checked, checkStyle, description, density: densityProp, details, disabled, endItems, groupLabel, hasNestedChildren, hasAdditionalActions: hasAdditionalActionsProp, id, icon, inputProps: inputPropsProp, inputType, items, isActive, isExpanded, isSelected, label, labelString = typeof label === "string" ? label : undefined, linkProps, nestLevel, onClick, onIconClick, onNextClick, selected, stringMap: stringMapProp, value, ...otherProps } = props;
    const stringMap = useStringMap(componentName, {
        stringMapDefault,
        stringMapProp: stringMapProp
            ? {
                // a way to mitigate deprecated prop usage
                ...(stringMapProp?.infoIcon ? { detailsLabel: stringMapProp?.infoIcon } : undefined),
                ...stringMapProp
            }
            : stringMapProp
    });
    const { listId, mode, density: densityContext } = useContext(MenuListContext);
    const density = densityProp || densityContext;
    const itemRef = useRef(null);
    // FIXME: uniqueId isn't stable.
    const itemId = (() => {
        if (id) {
            return id;
        }
        return listId ? `${listId}${value ? scrubStringForId(value) : uniqueId("option")}` : uniqueId("option");
    })();
    const labelId = `${itemId}-label`;
    const descId = `${itemId}-desc`;
    const hintId = `${itemId}-hint`;
    const inputProps = inputPropsProp || {};
    const { handleClick, handleIconClick, handleKeyDown, hasChildren, roleStateProps } = useMenuListItem(mode, {
        ...props,
        id: itemId
    });
    const isAtomicMode = mode === "menu" || mode === "select";
    const itemRole = isAtomicMode ? "none" : "listitem";
    // TODO: warn about using extra controls in menu or select mode
    const navigationElement = useNavigationElement({
        mode,
        label,
        linkProps,
        isActive,
        onKeyDown: handleKeyDown,
        onClick: handleClick
    });
    const isAtomicPrimaryElement = navigationElement !== undefined;
    const hint = (() => {
        if ((isAtomicMode || !isAtomicPrimaryElement) && hasAdditionalActionsProp) {
            return stringMap.hasAdditionalActionsHint;
        }
        if (isAtomicMode && details) {
            return stringMap.hasDetailsHint;
        }
        // FIXME: should also work with multiselect and checkbox items in interactive but get applied to the checkbox description rather than atomic controls
        if (!isAtomicMode && hasChildren) {
            return stringMap.hasChildrenHint;
        }
        return undefined;
    })();
    const handleClickThrough = useCallback((event) => {
        if (hasChildren &&
            event.nativeEvent.target?.closest(`[data-testid="MenuListItem"]`)?.id !== itemId) {
            return;
        }
        if ((mode === "navigation" || mode === "interactive") && isAtomicPrimaryElement) {
            const link = itemRef.current?.querySelector("a:link[data-primary-focus-target]");
            // emulate link click
            // ts hack to narrow type down to HTMLElement
            if (link) {
                link.click();
                return;
            }
        }
        handleClick(event);
    }, [handleClick, hasChildren, isAtomicPrimaryElement, itemId, mode]);
    const handleItemClickThrough = useClickThrough(itemRef, handleClickThrough);
    const { content } = (() => {
        const defaultContents = (_jsxs(_Fragment, { children: [_jsxs(Styled.PrimaryAction
                // most modes will use it as button, but "navigation" and "interactive" can render their own nested element
                , { 
                    // most modes will use it as button, but "navigation" and "interactive" can render their own nested element
                    as: isAtomicPrimaryElement ? "div" : undefined, hasIcon: Boolean(icon), ...(isAtomicPrimaryElement
                        ? {}
                        : {
                            ...roleStateProps,
                            onClick: handleClick,
                            onKeyDown: handleKeyDown,
                            "aria-labelledby": [labelId].filter(Boolean).join(" "),
                            "aria-describedby": [hint && hintId, description && descId].filter(Boolean).join(" "),
                            "data-primary-focus-target": "true"
                        }), children: [icon && _jsx(Styled.IconWrapper, { "aria-hidden": "true", children: icon }), icon === undefined && (_jsx(Styled.SelectedIconWrapper, { "aria-hidden": "true", children: _jsx(Styled.SelectedIcon, { "aria-hidden": "true", size: 12, isSelected: selected ? selected : isSelected, palette: "interactiveConstructive" }) })), _jsx(Styled.Label, { id: labelId, children: navigationElement ?? label }), Boolean(hint) && (_jsxs(Styled.VisuallyHiddenLabel, { "aria-hidden": isAtomicPrimaryElement, id: hintId, children: [", ", hint] })), description && (_jsx(Styled.Description, { "aria-hidden": isAtomicPrimaryElement, id: descId, children: description }))] }), endItems && _jsx(Styled.EndWrapper, { children: endItems }), details && (
                // hide when starts scrolling out of viewport? what should be considered the scroll parent
                _jsx(Styled.Details, { role: "none", children: _jsx(MenuListItemDetails, { stringMap: stringMap, label: labelString, details: details }) })), hasChildren && (_jsx("span", { children: _jsx(Styled.ParentForwardWrapper, { "aria-hidden": isAtomicMode, as: isAtomicMode ? "span" : undefined, onClick: isAtomicMode ? undefined : handleIconClick || handleClick, children: _jsx(Styled.ParentForwardIcon, { "aria-label": stringMap.viewNestedAriaLabel }) }) })), isExpanded !== undefined && _jsx(Styled.ExpandIcon, { "aria-hidden": "true", isExpanded: isExpanded })] }));
        if (mode === "multiselect-with-checkboxes") {
            return {
                content: (_jsx(InteractiveMenuListItem, { endItems: endItems && _jsx(Styled.EndWrapper, { children: endItems }), label: label, handleIconClick: handleIconClick, hasChildren: hasChildren, labelString: labelString, nestLevel: nestLevel, parentId: listId, viewNested: stringMap.viewNestedAriaLabel, children: _jsx(MenuListItemCheckBox, { onChange: handleClick, onKeyDown: handleKeyDown, checked: checked, disabled: disabled, hasNestedChildren: hasNestedChildren, checkStyle: checkStyle, label: label, nestedGroupLabel: stringMap.nestedGroupLabel, value: value, inputProps: roleStateProps, ...inputProps }) }))
            };
        }
        if (mode === "menu" || mode === "interactive") {
            if (checked === undefined && !inputType) {
                return { content: defaultContents };
            }
            return {
                content: (_jsx(InteractiveMenuListItem, { endItems: endItems && _jsx(Styled.EndWrapper, { children: endItems }), label: label, handleIconClick: handleIconClick, hasChildren: hasChildren, labelString: labelString, nestLevel: nestLevel, parentId: listId, viewNested: stringMap.viewNestedAriaLabel, children: inputType === "radio" ? (_jsx(MenuListItemRadio, { isNarrow: true, checked: checked, disabled: disabled, label: label, value: value, onChange: handleClick, onKeyDown: handleKeyDown, inputProps: roleStateProps, ...inputProps })) : (_jsx(MenuListItemCheckBox, { isNarrow: true, checked: checked, disabled: disabled, hasNestedChildren: hasNestedChildren, checkStyle: checkStyle, label: label, nestedGroupLabel: stringMap.nestedGroupLabel, value: value, onChange: handleClick, onKeyDown: handleKeyDown, inputProps: roleStateProps, ...inputProps })) }))
            };
        }
        return { content: defaultContents };
    })();
    const StyledItem = mode === "navigation" && props.isSelectable !== false ? Styled.NavItem : Styled.Item;
    return (_jsx(StyledItem, { "data-testid": "MenuListItem", "data-menu-list-item": true, role: itemRole, isDisabled: disabled, isNarrow: Boolean(mode === "interactive" && (inputType || checked !== undefined)), density: density, isActive: isActive, isSelected: selected ? selected : isSelected, ref: combineRefs(ref, itemRef), onClick: handleItemClickThrough, ...otherProps, children: content }));
});
MenuListItem.displayName = componentName;
